/**
 * Living Elo — official World Football Elo Ratings formula (eloratings.net/about):
 *   Rn = Ro + K * G * (W - We)
 * K = 60 for World Cup finals (ALL matches, groups -> final).
 * Shoot-out matches count as a DRAW at the end-of-extra-time score (W = 0.5),
 * eloratings.net convention — NOT the FIFA 0.75/0.5 formula.
 */
import type { EloTable, FinishedMatch, HostsTable, TeamCode } from "./types.ts";

export const K_WORLD_CUP = 60;
/** eloratings.net standard home advantage, used for Elo UPDATES (specs/03). */
export const ELO_UPDATE_HOME_ADV = 100;
/** Host team -> country, mirrors the legacy HOSTS table. */
export const DEFAULT_HOSTS: HostsTable = { MEX: "mx", USA: "us", CAN: "ca" };

export interface EloUpdateOptions {
  readonly k?: number;
  readonly homeAdvantage?: number;
  readonly hosts?: HostsTable;
}

/** We = 1 / (10^(-dr/400) + 1) */
export function expectedResult(eloDiff: number): number {
  return 1 / (Math.pow(10, -eloDiff / 400) + 1);
}

/** G : 1 (win by 0-1 or draw), 1.5 (by 2), (11+N)/8 (by N >= 3). */
export function goalDiffMultiplier(goalDiff: number): number {
  const n = Math.abs(goalDiff);
  if (n <= 1) return 1;
  if (n === 2) return 1.5;
  return (11 + n) / 8;
}

export interface EloMatchUpdate {
  readonly id: number;
  readonly home: TeamCode;
  readonly away: TeamCode;
  /** Elo deltas actually applied (away delta is always -homeDelta). */
  readonly homeDelta: number;
  readonly homeBefore: number;
  readonly awayBefore: number;
  readonly homeAfter: number;
  readonly awayAfter: number;
}

/**
 * Effective Elo gap home - away, including home advantage when one side is a
 * host nation playing in ITS OWN country (decided by the stadium's country,
 * knockout rounds included — e.g. Mexico at the Azteca in a quarter-final).
 */
export function effectiveEloDiff(
  eloHome: number,
  eloAway: number,
  home: TeamCode,
  away: TeamCode,
  hostCountry: string | null | undefined,
  homeAdvantage: number,
  hosts: HostsTable,
): number {
  let dr = eloHome - eloAway;
  if (hostCountry) {
    if (hosts[home] === hostCountry) dr += homeAdvantage;
    if (hosts[away] === hostCountry) dr -= homeAdvantage;
  }
  return dr;
}

/** Update both ratings for one finished match. Pure. */
export function updateRatings(
  eloHome: number,
  eloAway: number,
  match: FinishedMatch,
  options: EloUpdateOptions = {},
): EloMatchUpdate {
  const k = options.k ?? K_WORLD_CUP;
  const homeAdvantage = options.homeAdvantage ?? ELO_UPDATE_HOME_ADV;
  const hosts = options.hosts ?? DEFAULT_HOSTS;

  const [gh, ga] = match.score;
  // A shoot-out decision counts as a draw at the end-of-ET score; by definition
  // that score is level, so W = 0.5 and G = 1 follow naturally from it.
  const w = gh > ga ? 1 : gh < ga ? 0 : 0.5;
  const g = goalDiffMultiplier(gh - ga);
  const dr = effectiveEloDiff(
    eloHome,
    eloAway,
    match.home,
    match.away,
    match.hostCountry,
    homeAdvantage,
    hosts,
  );
  const we = expectedResult(dr);
  const homeDelta = k * g * (w - we);
  return {
    id: match.id,
    home: match.home,
    away: match.away,
    homeDelta,
    homeBefore: eloHome,
    awayBefore: eloAway,
    homeAfter: eloHome + homeDelta,
    awayAfter: eloAway - homeDelta,
  };
}

export interface ApplyResultsOutput {
  /** New table (input is never mutated). */
  readonly elos: EloTable;
  /** One entry per applied match, in application order (ascending id). */
  readonly history: readonly EloMatchUpdate[];
}

/**
 * Apply every finished match in FIFA id order (1 -> 104). Pure and replayable:
 * same input always yields the same output (prefix-associative, tested).
 */
export function applyResults(
  initialElos: EloTable,
  finishedMatches: readonly FinishedMatch[],
  options: EloUpdateOptions = {},
): ApplyResultsOutput {
  const elos: Record<TeamCode, number> = { ...initialElos };
  const history: EloMatchUpdate[] = [];
  const ordered = [...finishedMatches].sort((a, b) => a.id - b.id);
  for (const match of ordered) {
    const eloHome = elos[match.home];
    const eloAway = elos[match.away];
    if (eloHome === undefined || eloAway === undefined) {
      throw new Error(`applyResults: unknown team in match ${match.id} (${match.home}-${match.away})`);
    }
    const update = updateRatings(eloHome, eloAway, match, options);
    elos[match.home] = update.homeAfter;
    elos[match.away] = update.awayAfter;
    history.push(update);
  }
  return { elos, history };
}
